import type { Ref } from 'vue'

import merge from 'lodash-es/merge'
import { useTippy } from 'vue-tippy'

export function useEllipsisTooltip(options?: Recordable) {
  const isOpenedTooltip: Ref<Recordable> = ref({})

  /**
   * @method createTooltip
   * @description When the container element is hovered, if the text inside is ellipsis with CSS, it shows a tooltip.
   * @param event     - MouseEvent | FocusEvent (Generally we use @mouseenter)
   * @param selector  - Holds selector for to search for element inside container. (Ellipsis text)
   * @param id        - Unique id kept to avoid recreating Tippy over and over again. If not given, it automatically takes the ellipsised text.
   */
  function createTooltip(
    event: MouseEvent | FocusEvent,
    selector?: string,
    id?: string | number,
  ) {
    const container = event.target as HTMLElement

    // If given any target, use that target. Otherwise, use container as target.
    const target: HTMLElement | null = selector
      ? container.querySelector(selector)
      : container

    // If there is not any target, exit this function
    if (!target || !target.textContent) return

    const tooltipId = id || target.textContent

    // If tippy is already created, do not create again. Exit the function.
    if (isOpenedTooltip.value[tooltipId]) return

    // If there is a target and its text ellipsis with CSS, creates tooltip.
    if (target.scrollWidth > target.offsetWidth) {
      const tooltipOptions = merge(
        {
          content: target.textContent,
          showOnCreate: true,
          theme: 'dark',
        },
        options,
      )

      useTippy(container, tooltipOptions)
      isOpenedTooltip.value[tooltipId] = true
    }
  }

  return { createTooltip }
}
